import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, TrendingUp, Leaf, Shield, Activity, RefreshCw, Mail, Lightbulb, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import StatCard from '../components/dashboard/StatCard';
import RecentBatches from '../components/dashboard/RecentBatches';
import { toast } from 'sonner';
import SectionBreak from '../components/optimar/SectionBreak';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, batchesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard`),
        axios.get(`${BACKEND_URL}/api/batches`)
      ]);
      setStats(statsRes.data);
      setBatches(batchesRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.success('Refreshing dashboard...');
    await fetchDashboardData();
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  };

  const getRoleName = (role) => {
    const roleMap = {
      supply_chain_manager: 'Supply Chain Manager',
      sustainability_manager: 'Sustainability Manager',
      external_viewer: 'External Viewer',
    };
    return roleMap[role] || role;
  };

  const getCO2Insights = () => {
    if (!batches || batches.length === 0) return [];
    
    const insights = [];
    const avgCO2 = batches.reduce((sum, b) => sum + b.total_co2_kg, 0) / batches.length;
    const highCO2Batches = batches.filter(b => b.total_co2_kg > avgCO2 * 1.2);
    
    if (highCO2Batches.length > 0) {
      const potentialSaving = highCO2Batches.reduce((sum, b) => sum + (b.total_co2_kg * 0.15), 0);
      insights.push({
        title: 'Route Optimization Opportunity',
        description: `${highCO2Batches.length} batch${highCO2Batches.length > 1 ? 'es' : ''} could benefit from optimized transport routes`,
        impact: `Potential saving: ${potentialSaving.toFixed(1)} kg CO₂ (~15%)`,
        icon: TrendingUp,
        color: 'bg-slate-50 border-slate-200 text-slate-800'
      });
    }
    
    const inTransit = batches.filter(b => b.current_status === 'In Transit');
    if (inTransit.length >= 2) {
      insights.push({
        title: 'Shipment Consolidation',
        description: `${inTransit.length} batches currently in transit could be consolidated`,
        impact: 'Reduce logistics CO₂ by 20-30%',
        icon: Package,
        color: 'bg-slate-50 border-slate-200 text-slate-800'
      });
    }
    
    const coldStorage = batches.filter(b => b.current_status === 'In Storage');
    if (coldStorage.length > 0) {
      insights.push({
        title: 'Storage Efficiency',
        description: `Optimize cold storage duration for ${coldStorage.length} batch${coldStorage.length > 1 ? 'es' : ''}`,
        impact: 'Reduce energy consumption by 8-12%',
        icon: Leaf,
        color: 'bg-slate-50 border-slate-200 text-slate-800'
      });
    }
    
    return insights.slice(0, 3);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 sm:space-y-12" data-testid="dashboard-page">
      {/* Header with Refresh */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Welcome back, {user.name}
          </h1>
          <p className="text-lg text-slate-600">
            {getRoleName(user.role)} Dashboard
          </p>
          {lastUpdated && (
            <p className="text-sm text-slate-500 mt-1">
              Last updated: {getRelativeTime(lastUpdated)}
            </p>
          )}
        </div>
        <Button 
          onClick={handleRefresh} 
          disabled={refreshing}
          variant="outline"
          data-testid="refresh-button"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
        <StatCard
          icon={Package}
          label="Total Batches"
          value={stats?.total_batches || 0}
          trend="+3 this week"
          trendUp={true}
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
        <StatCard
          icon={Activity}
          label="Active Batches"
          value={stats?.active_batches || 0}
          trend="In transit or processing"
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
        <StatCard
          icon={Leaf}
          label="Total CO₂ Tracked"
          value={`${stats?.total_co2_kg || 0} kg`}
          trend="-12% vs last month"
          trendUp={true}
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
        <StatCard
          icon={Shield}
          label="Compliance Rate"
          value={`${stats?.compliance_rate || 0}%`}
          trend={`Avg integrity: ${stats?.avg_integrity_score || 0}`}
          trendUp={true}
          iconColor="text-slate-700"
          bgColor="bg-slate-100"
        />
      </div>
      
      {/* CO₂ Provenance Note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-xs text-slate-600">
          <strong>CO₂ Data Provenance:</strong> Displayed CO₂ metrics are estimated values (PoC-level). Actual measurements may vary based on operational conditions.
        </p>
      </div>

      {/* Compliance Disclaimer */}
      <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
        <p className="text-xs text-slate-700 leading-relaxed">
          <strong>Compliance Disclaimer:</strong> Compliance status reflects the completeness, traceability, and verification of data recorded within the PureSustain platform. Regulatory certification remains the responsibility of accredited authorities.
        </p>
      </div>

      <SectionBreak data-testid="dashboard-break-before-operational" />

      {/* Role-specific insights */}
      {user.role === 'sustainability_manager' && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Leaf className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Sustainability Insight</h3>
              <p className="text-slate-700">
                Your batches show a 12% reduction in CO₂ emissions compared to last month.
                Continue optimizing transport routes and cold storage efficiency.
              </p>
              <p className="text-xs text-slate-600 mt-2 italic">
                Insights are indicative and intended to support operational planning. Actual outcomes may vary.
              </p>
            </div>
          </div>
        </div>
      )}

      {user.role === 'supply_chain_manager' && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Package className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Operational Status</h3>
              <p className="text-slate-700">
                {stats?.active_batches || 0} batches are currently in transit or processing.
                All supply chain events are verified with blockchain-recorded audit trails.
              </p>
            </div>
          </div>
        </div>
      )}

      {user.role === 'external_viewer' && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <Shield className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Audit Information</h3>
              <p className="text-slate-700">
                All displayed data is read-only and immutably recorded. External viewers do not modify, approve, or certify records. You have read-only access to complete traceability records and compliance documentation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CO₂ Optimization Insights Widget */}
      {(user.role === 'supply_chain_manager' || user.role === 'sustainability_manager') && getCO2Insights().length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Lightbulb className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                CO₂ Optimization Insights
              </h2>
              <p className="text-sm text-slate-600">Decision-support insights (PoC-level estimation)</p>
            </div>
          </div>
          
          <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs text-slate-700 leading-relaxed">
              Insights are indicative and intended to support operational planning. Actual outcomes may vary.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {getCO2Insights().map((insight, idx) => (
              <div key={idx} className={`rounded-lg border p-4 ${insight.color}`}>
                <div className="flex items-start gap-3">
                  <insight.icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold mb-1">{insight.title}</h3>
                    <p className="text-sm mb-2 opacity-90">{insight.description}</p>
                    <p className="text-xs font-medium opacity-75">{insight.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Batches */}
      <div>
        <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
          Recent Batches
        </h2>
        <RecentBatches batches={stats?.recent_batches || []} />
      </div>
    </div>
  );
};

export default Dashboard;
